import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ActiveUserService } from '../services/active-user.service';
import { AuthService } from '../services/auth.service';
import { getNextRequiredLegalGateDocument } from '../services/auth-navigation';
import { GlLegalAbsentTemplateService } from '../services/gl-legal-absent-template.service';
import { TripService } from '../services/trip.service';

/**
 * Blocks trip-scoped routes until required legal documents are signed.
 * Reads role and signature state from the authenticated user's trip payload.
 */
export const tripAgreementsGuard: CanActivateFn = (_route, state) => {
  const router = inject(Router);
  const tripService = inject(TripService);
  const activeUserService = inject(ActiveUserService);
  const authService = inject(AuthService);
  const absentTemplateService = inject(GlLegalAbsentTemplateService);

  if (activeUserService.isActingAsOther()) {
    return true;
  }

  if (tripService.currentTripId() === null) {
    return true;
  }

  if (!tripService.currentTripLoaded()) {
    return true;
  }

  const trip = tripService.authUserTrip.value();
  if (trip === undefined) {
    return true;
  }

  const tripId = tripService.currentTripId();
  const nextDocument = getNextRequiredLegalGateDocument(trip, {
    isActingAsOther: false,
    isStaffImpersonation: authService.isStaffImpersonation(),
    absentTemplateKeys: absentTemplateService.absentKeysForTrip(tripId),
    hasEnded: tripService.hasEnded(),
  });
  if (!nextDocument) {
    return true;
  }

  return router.createUrlTree(['/trip-agreements'], {
    queryParams: { returnUrl: state.url },
  });
};
